import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, Check, Loader2, Minimize2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { aiGatewayService } from '@/services/aiGateway';

/**
 * Fullscreen "we're generating your SOAP" surface, shown during the
 * 60–90s LLM + validation pipeline. Built as an overlay rather than a
 * separate route so callers (RecordingDock on Complete, EncounterReviewPage
 * on Regenerate) don't need to coordinate URL state with the in-flight
 * scribe.* socket events.
 *
 * Subscribes to the canonical pipeline events:
 *   scribe.processing-update    → "extracting clinical content…"
 *   scribe.validation-stage     → per-stage active/done/failed
 *   scribe.soap-result          → success → onResult fires
 *   scribe.error                → upstream failure → onError fires
 *
 * Scoped to a sessionId when supplied. Some events (regenerate) don't
 * carry a sessionId; we accept those when there's no scope to filter on.
 */

export interface SynthStage {
  id: string;
  label: { en: string; ar: string };
}

// Doctor-friendly copy. Each stage describes what's happening for the
// patient encounter, not the engineering pipeline. Hover/aria still
// surface the underlying stage id for support tickets, but the visible
// text reads like a clinical workflow, not a backend trace.
const DEFAULT_STAGES: SynthStage[] = [
  {
    id: 'extract',
    label: {
      en: 'Drafting note from your conversation',
      ar: 'صياغة الملاحظة من محادثتك',
    },
  },
  {
    id: 'schema',
    label: {
      en: 'Organizing into SOAP sections',
      ar: 'تنظيمها في أقسام SOAP',
    },
  },
  {
    id: 'verify',
    label: {
      en: 'Cross-checking what was said',
      ar: 'التحقق ممّا قيل في الزيارة',
    },
  },
  {
    id: 'drugs',
    label: {
      en: 'Reviewing medications mentioned',
      ar: 'مراجعة الأدوية المذكورة',
    },
  },
  {
    id: 'grounding',
    label: {
      en: 'Confirming everything came from the visit',
      ar: 'التأكد أن كل شيء مأخوذ من الزيارة',
    },
  },
  {
    // Real as of the red-flag rule engine (backend
    // common/ai-gateway/safety). Deliberately NOT called a "clinical safety
    // check": it runs a small, deterministic rule set over the transcript,
    // it is advisory, and it does not block signing. The previous label —
    // "Final clinical safety check" — was shown while NOTHING ran, which is
    // an explicit and false assurance to the clinician.
    id: 'safety',
    label: {
      en: 'Checking for urgent red flags',
      ar: 'فحص العلامات التحذيرية العاجلة',
    },
  },
];

type StageStatus = 'pending' | 'active' | 'done' | 'failed';

interface SoapResultPayload {
  sessionId?: string;
  medicalRecordId?: string;
  recordId?: string;
}

interface ScribeErrorPayload {
  message?: string;
  error?: string;
}

interface ValidationStagePayload {
  id?: string;
  status?: StageStatus;
  detail?: string;
  sessionId?: string;
}

export interface SynthOverlayProps {
  open: boolean;
  isRTL: boolean;
  /**
   * Title shown at the top — e.g. "Generating SOAP" for first-time gen,
   * "Regenerating with template" for the review-page case.
   */
  title: string;
  subtitle?: string;
  /**
   * Optional sessionId to scope incoming scribe events. Events that
   * carry a different sessionId are ignored. Pass null when there's no
   * scope to filter on (e.g. regenerate, which uses a synthetic
   * `regen_<ms>` sessionId not exposed to the FE).
   */
  sessionId?: string | null;
  /**
   * Live transcript preview. Shown collapsed inside the overlay so the
   * doctor can see what the model is working from. When the parent
   * receives a refined transcript via the post-process pass, it can
   * pass the new value here and the panel updates in place — that's
   * the "OR if changed it'd change too" affordance.
   */
  transcript?: string | null;
  /** Marks the visible transcript as the polished post-process version
   *  vs. the live partial. Drives the small badge on the preview. */
  transcriptIsRefined?: boolean;
  onResult: (payload: SoapResultPayload) => void;
  onError: (message: string) => void;
  /** Called when the user opts out of waiting. */
  onCancel?: () => void;
  /**
   * Optional retry. When provided, a failed or stalled run surfaces a
   * "Try again" button that re-dispatches the synth from the parent.
   */
  onRetry?: () => void;
  /**
   * "Continue in background" — dismiss the fullscreen surface and let
   * the synth keep running. Parent is expected to register the job in
   * the global synth-jobs store so the floating pill picks it up and
   * the doctor can navigate freely.
   */
  onMinimize?: () => void;
}

export const SynthOverlay: React.FC<SynthOverlayProps> = ({
  open,
  isRTL,
  title,
  subtitle,
  sessionId,
  transcript,
  transcriptIsRefined,
  onResult,
  onError,
  onCancel,
  onRetry,
  onMinimize,
}) => {
  // Visible progress is PACED, not a 1:1 mirror of backend events. The
  // post-extract stages fire in a burst (most do little real work) and the
  // soap-result lands almost simultaneously — applying them verbatim made the
  // modal jump straight from "extracting" to the finished page. Instead we
  // advance the visible step by at most one every MIN_STAGE_MS toward the
  // backend's true progress, and defer onResult until the steps visibly walk
  // through to the end.
  const MIN_STAGE_MS = 520;
  const [visibleStep, setVisibleStep] = useState(0); // completed stages; active = index visibleStep
  const [details, setDetails] = useState<Record<string, string | undefined>>({});
  const [failedStageId, setFailedStageId] = useState<string | null>(null);
  const [statusLabel, setStatusLabel] = useState<string | null>(null);
  // Set when no backend progress event has arrived for STALL_MS — the socket
  // may have silently died. Surfaces an honest "taking longer" state with an
  // explicit escape so the doctor is never staring at a wedged spinner.
  const [stalled, setStalled] = useState(false);
  // Wall-clock seconds since the overlay opened. Shown next to the stage
  // fraction so the wait is legible against the "usually 60–90 seconds"
  // copy — a doctor watching 2:30 tick past knows something's off long
  // before the stall watchdog would have to tell them.
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    if (!open) return;
    setElapsedSec(0);
    const startedAt = Date.now();
    const id = setInterval(
      () => setElapsedSec(Math.floor((Date.now() - startedAt) / 1000)),
      1_000,
    );
    return () => clearInterval(id);
  }, [open]);

  // Backend truth + deferred result, read by the pacing interval below.
  const realDoneRef = useRef(0);
  const resultRef = useRef<SoapResultPayload | null>(null);
  const resultFiredRef = useRef(false);
  // Timestamp of the last backend event; drives the stall watchdog.
  const lastEventRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    // Reset every time the overlay opens so a second regenerate doesn't
    // start with the prior run's stale stage badges.
    setVisibleStep(0);
    setDetails({});
    setFailedStageId(null);
    setStatusLabel(null);
    setStalled(false);
    realDoneRef.current = 0;
    resultRef.current = null;
    resultFiredRef.current = false;
    lastEventRef.current = Date.now();

    const sameSession = (incoming?: string | null) =>
      !sessionId || !incoming || incoming === sessionId;

    // Any backend signal means we're alive — note the time and clear a stall.
    const bump = () => {
      lastEventRef.current = Date.now();
      setStalled(false);
    };

    // Translate backend status strings into doctor-friendly copy. The
    // backend emits English engineering messages ("Generating SOAP note...",
    // "Finalizing transcription...") — surface them in clinical phrasing.
    const friendlyStatus = (raw: string | undefined): string | null => {
      if (!raw) return null;
      const lower = raw.toLowerCase();
      if (lower.includes('finaliz')) {
        return isRTL ? 'جمع كل المحادثة…' : 'Pulling the full conversation together…';
      }
      if (lower.includes('transcrib')) {
        return isRTL
          ? 'تفريغ الصوت إلى نص…'
          : 'Turning the recording into text…';
      }
      if (lower.includes('soap') || lower.includes('generat')) {
        return isRTL
          ? 'تجهيز ملاحظة الزيارة…'
          : 'Drafting your encounter note…';
      }
      // Fallback to the raw line — not ideal but better than dropping it.
      return raw;
    };

    const offProcessing = aiGatewayService.on(
      'scribe.processing',
      (data: { message?: string; sessionId?: string }) => {
        if (!sameSession(data?.sessionId)) return;
        bump();
        const label = friendlyStatus(data?.message);
        if (label) setStatusLabel(label);
      },
    );

    const offUpdate = aiGatewayService.on(
      'scribe.processing-update',
      (data: { status?: string; sessionId?: string }) => {
        if (!sameSession(data?.sessionId)) return;
        bump();
        if (data?.status === 'generating') {
          setStatusLabel(
            isRTL
              ? 'تجهيز ملاحظة الزيارة…'
              : 'Drafting your encounter note…',
          );
        }
      },
    );

    const offStage = aiGatewayService.on(
      'scribe.validation-stage',
      (data: ValidationStagePayload) => {
        if (!sameSession(data?.sessionId)) return;
        bump();
        if (!data?.id || !data?.status) return;
        const idx = DEFAULT_STAGES.findIndex((s) => s.id === data.id);
        if (idx === -1) return;
        if (data.detail) {
          setDetails((prev) => ({ ...prev, [data.id!]: data.detail }));
        }
        // Record backend truth only; the interval paces the visible step
        // toward it so each stage stays on screen long enough to read.
        if (data.status === 'failed') {
          setFailedStageId(data.id!);
        } else if (data.status === 'done') {
          realDoneRef.current = Math.max(realDoneRef.current, idx + 1);
        } else if (data.status === 'active') {
          realDoneRef.current = Math.max(realDoneRef.current, idx);
        }
      },
    );

    const offResult = aiGatewayService.on(
      'scribe.soap-result',
      (data: SoapResultPayload) => {
        if (!sameSession(data?.sessionId)) return;
        bump();
        // Result is ready — set all stages as the target, but DEFER onResult
        // until the paced animation has visibly walked through them (handled
        // by the effect watching visibleStep). This is what stops the modal
        // jumping straight from "extracting" to the finished page.
        resultRef.current = data;
        realDoneRef.current = DEFAULT_STAGES.length;
      },
    );

    const offError = aiGatewayService.on(
      'scribe.error',
      (data: ScribeErrorPayload) => {
        // scribe.error often doesn't carry sessionId — treat undefined as
        // "for us" since there's no other consumer in this component tree.
        const msg = data?.message ?? (isRTL ? 'فشل التوليد' : 'Generation failed');
        onError(msg);
      },
    );

    // Pacing driver — advance one stage at a time toward backend truth, so
    // the burst of near-instant stages (and the result) plays out visibly.
    const timer = setInterval(() => {
      setVisibleStep((prev) => (prev < realDoneRef.current ? prev + 1 : prev));
    }, MIN_STAGE_MS);

    // Stall watchdog — if no backend event has landed for STALL_MS and the
    // result hasn't arrived, the pipeline (or the socket) is likely wedged.
    // Flip to a stalled state so the doctor gets an honest message + an exit
    // instead of an indefinite spinner.
    const STALL_MS = 45_000;
    const watchdog = setInterval(() => {
      if (resultRef.current || resultFiredRef.current) return;
      if (Date.now() - lastEventRef.current > STALL_MS) {
        setStalled(true);
      }
    }, 5_000);

    return () => {
      clearInterval(timer);
      clearInterval(watchdog);
      offProcessing();
      offUpdate();
      offStage();
      offResult();
      offError();
    };
  }, [open, sessionId, isRTL, onResult, onError]);

  // Fire onResult only once the paced steps have finished AND the backend
  // result has actually arrived — never mid-animation.
  useEffect(() => {
    if (
      visibleStep >= DEFAULT_STAGES.length &&
      resultRef.current &&
      !resultFiredRef.current
    ) {
      resultFiredRef.current = true;
      onResult(resultRef.current);
    }
  }, [visibleStep, onResult]);

  if (!open) return null;

  const failedIdx = failedStageId
    ? DEFAULT_STAGES.findIndex((s) => s.id === failedStageId)
    : -1;
  const orderedStages = DEFAULT_STAGES.map((s, i) => {
    let status: StageStatus;
    if (failedIdx === i) status = 'failed';
    else if (i < visibleStep) status = 'done';
    else if (i === visibleStep && failedIdx === -1) status = 'active';
    else status = 'pending';
    return { ...s, state: { status, detail: details[s.id] } };
  });
  const completedCount = orderedStages.filter((s) => s.state.status === 'done').length;
  const total = orderedStages.length;
  const failedStage = orderedStages.find((s) => s.state.status === 'failed');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--color-bg-primary) 96%, transparent)',
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div
        className="w-full max-w-xl rounded-2xl p-6 sm:p-8 space-y-5"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          border: '1px solid var(--color-border-primary)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
        }}
      >
        <header className="space-y-1">
          <div
            className="inline-flex items-center gap-2"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            <span aria-hidden className="w-6 h-px" style={{ backgroundColor: '#c9ad85' }} />
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold">
              {isRTL ? 'جاري المعالجة' : 'Processing'}
            </span>
          </div>
          <h1
            className="text-xl sm:text-2xl font-semibold tracking-tight"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              {subtitle}
            </p>
          )}
        </header>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div
              className="text-xs font-medium"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {failedStage
                ? (isRTL ? 'حدثت مشكلة' : 'Something went wrong')
                : statusLabel ??
                  (isRTL
                    ? 'قد يستغرق ذلك ٦٠–٩٠ ثانية'
                    : 'Usually takes 60–90 seconds')}
            </div>
            <div
              className="text-xs font-mono tabular-nums"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              {`${Math.floor(elapsedSec / 60)}:${(elapsedSec % 60)
                .toString()
                .padStart(2, '0')}`}
              {' · '}
              {completedCount}/{total}
            </div>
          </div>
          <div
            className="h-1.5 w-full overflow-hidden rounded-full"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--color-primary-600) 12%, transparent)',
            }}
          >
            <div
              className="h-full transition-all"
              style={{
                width: failedStage ? '100%' : `${(completedCount / total) * 100}%`,
                backgroundColor: failedStage
                  ? 'var(--color-error)'
                  : 'var(--color-primary-600)',
                transitionDuration: '300ms',
              }}
            />
          </div>
        </div>

        <ul className="space-y-2">
          {orderedStages.map((s) => (
            <li
              key={s.id}
              className="flex items-start gap-3 px-3 py-2 rounded-lg"
              style={{
                backgroundColor:
                  s.state.status === 'active'
                    ? 'color-mix(in srgb, var(--color-primary-600) 6%, transparent)'
                    : 'transparent',
              }}
            >
              <StageIcon status={s.state.status} />
              <div className="flex-1 min-w-0">
                <div
                  className="text-sm font-medium"
                  style={{
                    color:
                      s.state.status === 'failed'
                        ? 'var(--color-error)'
                        : 'var(--color-text-primary)',
                  }}
                >
                  {isRTL ? s.label.ar : s.label.en}
                </div>
                {s.state.detail && (
                  <div
                    className="text-[11px] mt-0.5"
                    style={{ color: 'var(--color-text-tertiary)' }}
                  >
                    {s.state.detail}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>

        {transcript && transcript.trim().length > 0 && (
          <details
            className="rounded-lg px-3 py-2"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-primary)',
            }}
          >
            <summary
              className="flex items-center gap-2 text-xs font-semibold cursor-pointer list-none"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              <span className="flex-1">
                {isRTL
                  ? 'النص الذي يستخدمه الذكاء الاصطناعي'
                  : 'What the AI is reading from'}
              </span>
              {transcriptIsRefined ? (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide"
                  style={{
                    backgroundColor:
                      'color-mix(in srgb, var(--color-success) 18%, transparent)',
                    color: 'var(--color-success)',
                  }}
                >
                  <Sparkles className="w-3 h-3" />
                  {isRTL ? 'محسّن' : 'Refined'}
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide"
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  {isRTL ? 'مباشر' : 'Live'}
                </span>
              )}
            </summary>
            <p
              className="mt-2 text-xs whitespace-pre-wrap max-h-40 overflow-y-auto"
              style={{ color: 'var(--color-text-secondary)', lineHeight: 1.55 }}
            >
              {transcript}
            </p>
          </details>
        )}

        {/* Attention state — a failed stage or a silent stall. Both leave the
            doctor needing a way out + a way to retry, which the in-progress
            footer below doesn't offer. */}
        {(failedStage || stalled) && (
          <div
            className="flex flex-col gap-3 px-3 py-3 rounded-lg"
            style={{
              backgroundColor: failedStage
                ? 'color-mix(in srgb, var(--color-error) 8%, transparent)'
                : 'color-mix(in srgb, var(--color-warning) 10%, transparent)',
              border: `1px solid ${
                failedStage
                  ? 'color-mix(in srgb, var(--color-error) 25%, transparent)'
                  : 'color-mix(in srgb, var(--color-warning) 30%, transparent)'
              }`,
            }}
          >
            <div className="flex items-start gap-2">
              <AlertCircle
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                style={{
                  color: failedStage
                    ? 'var(--color-error)'
                    : 'var(--color-warning)',
                }}
              />
              <p
                className="text-xs"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                {failedStage
                  ? isRTL
                    ? 'تعذّر إكمال إنشاء الملاحظة. يمكنك إعادة المحاولة أو المتابعة لاحقًا.'
                    : 'We couldn’t finish the note. You can try again, or come back to it later.'
                  : isRTL
                    ? 'يستغرق هذا وقتًا أطول من المعتاد. قد تكون الشبكة بطيئة. يمكنك الانتظار أو الخروج.'
                    : 'This is taking longer than usual — the connection may be slow. You can keep waiting or step out.'}
              </p>
            </div>
            <div className="flex items-center justify-end gap-2">
              {stalled && !failedStage && (
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => setStalled(false)}
                >
                  {isRTL ? 'متابعة الانتظار' : 'Keep waiting'}
                </Button>
              )}
              {onRetry && (
                <Button type="button" size="xs" onClick={onRetry}>
                  {isRTL ? 'إعادة المحاولة' : 'Try again'}
                </Button>
              )}
              {(onMinimize || onCancel) && (
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={onMinimize ?? onCancel}
                >
                  {isRTL ? 'إغلاق' : 'Close'}
                </Button>
              )}
            </div>
          </div>
        )}

        {!failedStage && !stalled && (onCancel || onMinimize) && (
          <div className="flex justify-between items-center gap-3 pt-2">
            <p
              className="text-[11px]"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              {onMinimize
                ? isRTL
                  ? 'يمكنك متابعة عملك. سنخبرك عند الانتهاء.'
                  : 'You can keep working. We’ll let you know when it’s ready.'
                : ''}
            </p>
            <div className="flex items-center gap-2">
              {onMinimize && (
                <Button
                  type="button"
                  size="xs"
                  onClick={onMinimize}
                >
                  <Minimize2 />
                  {isRTL ? 'متابعة في الخلفية' : 'Continue in background'}
                </Button>
              )}
              {onCancel && !onMinimize && (
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={onCancel}
                >
                  {isRTL ? 'إخفاء' : 'Hide'}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StageIcon: React.FC<{ status: StageStatus }> = ({ status }) => {
  if (status === 'done') {
    return (
      <span
        className="w-5 h-5 rounded-full inline-flex items-center justify-center flex-shrink-0"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--color-success) 18%, transparent)',
          color: 'var(--color-success)',
        }}
      >
        <Check className="w-3 h-3" />
      </span>
    );
  }
  if (status === 'active') {
    return (
      <Loader2
        className="w-5 h-5 animate-spin flex-shrink-0"
        style={{ color: 'var(--color-primary-600)' }}
      />
    );
  }
  if (status === 'failed') {
    return (
      <AlertCircle
        className="w-5 h-5 flex-shrink-0"
        style={{ color: 'var(--color-error)' }}
      />
    );
  }
  return (
    <span
      className="w-5 h-5 rounded-full border-2 flex-shrink-0"
      style={{
        borderColor: 'color-mix(in srgb, var(--color-text-tertiary) 40%, transparent)',
      }}
    />
  );
};
